import React, { useRef, useState } from 'react';
import { NumberArray } from '@wonderlandengine/api';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface BespokeColourPickerProps {
	initialColour?: NumberArray;
	onColourChanged: (rgba: NumberArray) => void;
}

interface HsvColour {
	hue: number;			// 0–360.
	saturation: number;		// 0–1.
	value: number;			// 0–1.
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

// the default position of the picker when no initial colour is supplied (a mid-blue).
const DEFAULT_HUE = 210;
const DEFAULT_SATURATION = 0.6;
const DEFAULT_VALUE = 0.75;

// that same default as an rgb colour, exported so that a host view can show the colour the picker will open on before anything has been chosen.
export const DEFAULT_BESPOKE_COLOUR: NumberArray = hsvToRgb(DEFAULT_HUE, DEFAULT_SATURATION, DEFAULT_VALUE);

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

/**
 * Bespoke Colour Picker:
 *
 * A self-contained HSV colour picker - a 2D saturation/brightness square and a hue slider, both draggable - that
 * reports the chosen colour as an RGBA array (0–1 floats) via the 'onColourChanged' callback. Used to let the
 * customer choose an arbitrary body colour rather than one of the pre-authored paint swatches.
 */
export default function BespokeColourPicker(props: BespokeColourPickerProps) {

	// derive the initial hue, saturation and value from the given colour, or from the default colour.
	const initialHsv = rgbToHsv(props.initialColour ?? DEFAULT_BESPOKE_COLOUR);

	// states.
	const [hue, setHue] = useState(initialHsv.hue);
	const [saturation, setSaturation] = useState(initialHsv.saturation);
	const [value, setValue] = useState(initialHsv.value);

	// element references, used to translate a pointer position into a 0–1 coordinate.
	const svAreaRef = useRef<HTMLDivElement>(null);
	const hueSliderRef = useRef<HTMLDivElement>(null);

	// dragging flags, so that pointer moves only update the picker while the relevant control is held.
	const isDraggingSvRef = useRef(false);
	const isDraggingHueRef = useRef(false);

	// the current colour as a hex string, for the sv handle preview.
	const currentHex = rgbToHex(hsvToRgb(hue, saturation, value));

	// handlers.

	// emit the given hue, saturation and value as an rgba colour.
	const emitColour = (h: number, s: number, v: number) => {
		const rgb = hsvToRgb(h, s, v);
		props.onColourChanged([rgb[0], rgb[1], rgb[2], 1]);
	};

	// update the saturation and value from a pointer position over the sv area.
	const updateSaturationValue = (clientX: number, clientY: number) => {

		// translate the pointer position into a 0–1 coordinate within the sv area.
		const rect = svAreaRef.current!.getBoundingClientRect();
		const s = clamp((clientX - rect.left) / rect.width, 0, 1);
		const v = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);

		// update the states and emit the new colour.
		setSaturation(s);
		setValue(v);
		emitColour(hue, s, v);
	};

	// update the hue from a pointer position over the hue slider.
	const updateHue = (clientX: number) => {

		// translate the pointer position into a hue angle.
		const rect = hueSliderRef.current!.getBoundingClientRect();
		const h = clamp((clientX - rect.left) / rect.width, 0, 1) * 360;

		// update the state and emit the new colour.
		setHue(h);
		emitColour(h, saturation, value);
	};

	// begin dragging the sv area, capturing the pointer so moves continue outside the element.
	const handleSvPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		isDraggingSvRef.current = true;
		event.currentTarget.setPointerCapture(event.pointerId);
		updateSaturationValue(event.clientX, event.clientY);
	};

	const handleSvPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!isDraggingSvRef.current) return;
		updateSaturationValue(event.clientX, event.clientY);
	};

	const handleSvPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
		isDraggingSvRef.current = false;
		event.currentTarget.releasePointerCapture(event.pointerId);
	};

	// begin dragging the hue slider, capturing the pointer so moves continue outside the element.
	const handleHuePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		isDraggingHueRef.current = true;
		event.currentTarget.setPointerCapture(event.pointerId);
		updateHue(event.clientX);
	};

	const handleHuePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!isDraggingHueRef.current) return;
		updateHue(event.clientX);
	};

	const handleHuePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
		isDraggingHueRef.current = false;
		event.currentTarget.releasePointerCapture(event.pointerId);
	};

	// return the HTML.
	return (
		<div className="bespoke-colour-picker">

			<div
				ref={svAreaRef}
				className="bespoke-colour-picker-sv-area"
				style={{ backgroundColor: `hsl(${hue}, 100%, 50%)` }}
				title="Drag to adjust the saturation and brightness"
				onPointerDown={handleSvPointerDown}
				onPointerMove={handleSvPointerMove}
				onPointerUp={handleSvPointerUp}
			>
				<div
					className="bespoke-colour-picker-sv-handle"
					style={{ left: `${saturation * 100}%`, top: `${(1 - value) * 100}%`, backgroundColor: currentHex }}
				/>
			</div>

			<div
				ref={hueSliderRef}
				className="bespoke-colour-picker-hue-slider"
				title="Drag to adjust the hue"
				onPointerDown={handleHuePointerDown}
				onPointerMove={handleHuePointerMove}
				onPointerUp={handleHuePointerUp}
			>
				<div
					className="bespoke-colour-picker-hue-handle"
					style={{ left: `${(hue / 360) * 100}%`, backgroundColor: `hsl(${hue}, 100%, 50%)` }}
				/>
			</div>

		</div>
	);
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

// convert an RGB colour (0–1 float channels) to a hex string, so that a host view can show the chosen colour on a swatch of its own.
export function rgbToHex(rgb: NumberArray): string {
	return '#' + [rgb[0], rgb[1], rgb[2]]
		.map((channel) => Math.round(clamp(channel, 0, 1) * 255).toString(16).padStart(2, '0'))
		.join('');
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// clamp the given value between the given minimum and maximum.
function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

// convert an HSV colour to an RGB colour, returning the red, green and blue channels as 0–1 floats (as used by the material colours).
function hsvToRgb(hue: number, saturation: number, value: number): NumberArray {

	// work out which 60° segment of the colour wheel the hue falls in, and how far through it.
	const sector = (hue % 360) / 60;
	const chroma = value * saturation;
	const secondary = chroma * (1 - Math.abs((sector % 2) - 1));
	const match = value - chroma;

	// pick the red, green and blue channels for the segment.
	let r = 0, g = 0, b = 0;
	if (sector < 1) { r = chroma; g = secondary; }
	else if (sector < 2) { r = secondary; g = chroma; }
	else if (sector < 3) { g = chroma; b = secondary; }
	else if (sector < 4) { g = secondary; b = chroma; }
	else if (sector < 5) { r = secondary; b = chroma; }
	else { r = chroma; b = secondary; }

	// add the match value back on to lift the channels to the correct brightness.
	return [r + match, g + match, b + match];
}

// convert an RGB colour (0–1 float channels) to an HSV colour.
function rgbToHsv(rgb: NumberArray): HsvColour {

	// find the largest and smallest channels.
	const r = rgb[0], g = rgb[1], b = rgb[2];
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;

	// derive the hue from whichever channel is dominant.
	let hue = 0;
	if (delta !== 0) {
		if (max === r) {
			hue = ((g - b) / delta) % 6;
		}
		else if (max === g) {
			hue = (b - r) / delta + 2;
		}
		else {
			hue = (r - g) / delta + 4;
		}
		hue *= 60;
		if (hue < 0) {
			hue += 360;
		}
	}

	// saturation is the relative spread of the channels; value is the brightest channel.
	const saturation = max === 0 ? 0 : delta / max;
	return { hue, saturation, value: max };
}
